// src/domains/finance/payments/view/finance-payments-view.jsx

import { CardLoader } from 'components/organisms'
import withLoader from 'hocs/with-loader'
import PropTypes from 'prop-types'
import {
  Alert,
  Button,
  Card,
  CardBody,
  Collapse,
  Input,
  InputGroup,
  Label,
  ListGroup,
  ListGroupItem,
  Table
} from 'reactstrap'
import { useEffect, useMemo, useState } from 'react'
import StringUtil from 'utils/string-util'
import { formatThousandsInput, sanitizeThousandsInput } from 'utils/thousands-input-util'
import { PAYMENT_METHOD } from 'domains/sales/deals/constants'
import { PAYMENT_STATE } from '../constants'
import TimeUtil from 'utils/time-util'
import TableUtil from 'utils/table-util'

const propTypes = {
  payment: PropTypes.object,
  fetchPayment: PropTypes.func,
  updateDownPayment: PropTypes.func,
  confirmManualTransfer: PropTypes.func,
  rejectManualTransfer: PropTypes.func,
  confirmDownPayment: PropTypes.func,
  fetchHistoryList: PropTypes.func,
  historyList: PropTypes.array,
  fetchHistoryDetail: PropTypes.func,
  historyDetail: PropTypes.object,
  clearHistoryDetail: PropTypes.func,
}

const defaultValues = {
  payment: {},
  fetchPayment: () => {},
  updateDownPayment: () => {},
  confirmManualTransfer: () => {},
  rejectManualTransfer: () => {},
  confirmDownPayment: () => {},
  fetchHistoryList: () => {},
  historyList: [],
  fetchHistoryDetail: () => {},
  historyDetail: {},
  clearHistoryDetail: () => {},
}

const normalizeDealPayments = (deal) => {
  const list = Array.isArray(deal?.payments) ? deal.payments.filter(Boolean) : []
  if (list.length > 0) return list
  if (deal?.payment) return [deal.payment]
  return []
}

const sortPaymentsAsc = (payments) => {
  return (payments || []).slice().sort((a, b) => {
    const ta = new Date(a?.created_at || a?.updated_at || 0).getTime()
    const tb = new Date(b?.created_at || b?.updated_at || 0).getTime()
    return ta - tb
  })
}

const receiptPaymentTitle = (entry) => `Payment #${entry.payment_number} — ${entry.reference_number || '-'} — ${entry.state || '-'}${entry.current ? ' (sedang dibuka)' : ''}`

const isPdfUrl = (url = '') => url.toLowerCase().endsWith('.pdf')

const MANUAL_CONFIRMABLE_PAYMENT_METHODS = [
  PAYMENT_METHOD.MANUAL_TRANSFER,
  PAYMENT_METHOD.CASH,
  PAYMENT_METHOD.EDC,
  PAYMENT_METHOD.EDC_BRI,
  PAYMENT_METHOD.EDC_BCA,
  PAYMENT_METHOD.SOLD_IN_STORE,
]

const canShowManualActionButtons = (methodType) =>
  MANUAL_CONFIRMABLE_PAYMENT_METHODS.includes(methodType)

const paymentCardTypeLabel = (methodType = '', cardType = '') => {
  const normalizedMethod = (methodType || '').toString().trim().toLowerCase()
  const normalizedCardType = (cardType || '').toString().trim().toLowerCase()

  if (normalizedMethod === PAYMENT_METHOD.CREDIT_CARD) {
    if (normalizedCardType === 'amex') return 'Amex'
    if (normalizedCardType === 'non_amex') return 'Non Amex'
    return '-'
  }

  if (normalizedMethod === PAYMENT_METHOD.EDC_BCA || normalizedMethod === PAYMENT_METHOD.EDC_BRI || normalizedMethod === PAYMENT_METHOD.EDC) {
    if (normalizedCardType === 'visa_mastercard_on_us') return 'Visa/Mastercard on-us'
    if (normalizedCardType === 'visa_mastercard_off_us') return 'Visa/Mastercard off-us'
    if (normalizedCardType === 'jcb_union_pay') return 'JCB/Union Pay'
    if (normalizedCardType === 'amex') return 'Amex'
    if (normalizedCardType === 'debit_on_us') return 'Debit on-us'
    if (normalizedCardType === 'debit_off_us') return 'Debit off-us'
  }

  return cardType ? StringUtil.capitalizeAll(StringUtil.humanize(cardType)) : '-'
}

const actionStyles = {
  actionBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginTop: 12
  },
  singleActionBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center',
    justifyContent: 'flex-start'
  },
  button: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 140
  }
}

const FinancePaymentsView = ({
  payment,
  fetchPayment,
  updateDownPayment,
  confirmManualTransfer,
  rejectManualTransfer,
  confirmDownPayment,
  fetchHistoryList,
  historyList,
  fetchHistoryDetail,
  historyDetail,
  clearHistoryDetail,
}) => {
  const deal = payment?.deal || {}

  const hasPoOrBkoItem = useMemo(() => {
    const items = deal?.cart?.cart_items || []

    return items.some(ci => {
      const sku = (ci?.product?.sku || '').toUpperCase()
      return sku.startsWith('PO-')
    })
  }, [deal])

  // PO: we treat any deal with down_payment > 0 as PO/DP flow (your current convention)
  const isPoDpDeal = hasPoOrBkoItem

  const canConfirmDp =
    hasPoOrBkoItem &&
    deal?.state === 'waiting_for_confirmation'

  const [downPayment, setDownPayment] = useState(0)
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)
  const [showDifferenceOnly, setShowDifferenceOnly] = useState(false)

  useEffect(() => {
    setDownPayment(deal?.down_payment || 0)
  }, [deal?.down_payment])

  useEffect(() => {
    if (isHistoryOpen) fetchHistoryList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHistoryOpen])

  const hasAnyDownPayment = Number(deal?.down_payment ?? 0) > 0
  const shouldShowDownPaymentCard = true

  const tableData = TableUtil.convertHistoryDetailIntoTableData(historyDetail)

  const paymentsAsc = useMemo(() => (
    Array.isArray(payment?.deal_payment_receipts) && payment.deal_payment_receipts.length > 0
      ? payment.deal_payment_receipts.map((entry) => ({ ...entry, id: entry.payment_id }))
      : sortPaymentsAsc(normalizeDealPayments(deal))
  ), [deal, payment?.deal_payment_receipts])
  const paymentIndex = useMemo(() => {
    if (!payment?.id) return null
    const idx = paymentsAsc.findIndex((p) => p?.id === payment.id)
    return idx >= 0 ? idx + 1 : null
  }, [paymentsAsc, payment?.id])

  const currentReceiptEntry = payment?.deal_payment_receipts?.find((entry) => entry.payment_id === payment?.id)
  const currentReceiptTitle = currentReceiptEntry ? receiptPaymentTitle(currentReceiptEntry) : `Payment ${paymentIndex ? `#${paymentIndex}` : ''} — ${payment?.reference_number || '-'} — ${payment?.state || '-'} (sedang dibuka)`

  const dpShownInPayments = payment?.deal_payment_receipts?.some((entry) => entry.receipts?.some((file) => file.source === 'deal_dp'))
  const totalPayments = paymentsAsc.length || null
  
  const dpReceiptUrls = useMemo(() => {
    return Array.isArray(payment?.down_payment_proof_urls)
      ? payment.down_payment_proof_urls.filter(Boolean)
      : []
  }, [payment])

  // Receipts should NEVER disappear after actions:
  // we always call fetchPayment() after any mutation.
  const receiptUrls = useMemo(() => {
    const arr = Array.isArray(payment?.all_receipt_urls)
      ? payment.all_receipt_urls.filter(Boolean)
      : []
    if (arr.length) return arr
    if (payment?.transfer_receipt_url) return [payment.transfer_receipt_url]
    return []
  }, [payment])

  // ===============================
  // 🔒 PAYMENT LOCK & VALIDATION FLAGS
  // ===============================

  const finalAmount = Number(deal?.final_payment || 0)
  const currentDpAmount = Number(downPayment || 0)

  const hasDpProof = (dpReceiptUrls || []).length > 0
  const hasPaymentReceipt = (receiptUrls || []).length > 0
  const hasAnyFinanceProof = hasDpProof || hasPaymentReceipt

  const isSettlement =
    payment?.state === PAYMENT_STATE.DP_CONFIRMED ||
    payment?.state === PAYMENT_STATE.SETTLEMENT ||
    payment?.state === PAYMENT_STATE.CAPTURE

  // DP full logic
  const isDpEqualFinal =
    finalAmount > 0 && currentDpAmount === finalAmount

  // Full payment confirm allowed
  const canConfirmFullPaymentFrontend =
    hasPaymentReceipt &&
    !isSettlement &&
    isDpEqualFinal

  // ---------- Action wrappers (IMPORTANT) ----------
  const handleSaveDownPayment = async () => { await updateDownPayment(false, downPayment) }

  const handleConfirmFullPayment = async () => { await updateDownPayment(true, downPayment) }

  const handleConfirmDpMoveToBuying = async () => { await confirmDownPayment() }

  const handleRejectManual = async () => { await rejectManualTransfer() }

  async function handleConfirmManual() { await confirmManualTransfer() }

  return (
    <>
      {/* 1) SUMMARY */}
      <Card>
        <div className="p-3">
          <h2 className="mb-3 text-center">
            {`Summary (#${payment?.id || '-'}) - ${payment?.reference_number || '-'} - ${
              deal?.reference_number || '-'
            }`}
            {paymentIndex && totalPayments ? (
              <div className="text-muted" style={{ fontSize: 14, marginTop: 6 }}>
                {`Payment #${paymentIndex}/${totalPayments}`}
              </div>
            ) : null}
          </h2>

          <div style={actionStyles.singleActionBar}>
            <Button onClick={fetchPayment} color="info" style={actionStyles.button}>
              Refresh Data
            </Button>
          </div>

          <Table bordered responsive striped>
            <tbody>
              <tr>
                <th scope="row" className="text-right">
                  Final Payment
                </th>
                <td>{StringUtil.currencyCase(deal?.final_payment)}</td>
              </tr>
              <tr>
                <th scope="row" className="text-right">
                  Deal Status
                </th>
                <td>{StringUtil.capitalizeAll(StringUtil.humanize(deal?.state))}</td>
              </tr>
              <tr>
                <th scope="row" className="text-right">
                  Pembayaran
                </th>
                <td>{StringUtil.capitalizeAll(StringUtil.humanize(payment?.method_type))}</td>
              </tr>
              <tr>
              <th scope="row" className="text-right">
                  Card Type
                </th>
                <td>
                   {paymentCardTypeLabel(payment?.method_type, payment?.card_type)}
                </td>
              </tr>
              <tr>
                <th scope="row" className="text-right">
                  Payment State
                </th>
                <td>
                  {payment?.state ? StringUtil.capitalizeAll(StringUtil.humanize(payment.state)) : '-'}
                </td>
              </tr>
              <tr>
                <th scope="row" className="text-right">
                  User Email
                </th>
                <td>{payment?.user?.email || '-'}</td>
              </tr>
              <tr>
                <th scope="row" className="text-right">
                  User Full Name
                </th>
                <td>{payment?.user?.detail?.name || '-'}</td>
              </tr>
              <tr>
                <th scope="row" className="text-right">
                  Down Payment
                </th>
                <td>{StringUtil.currencyCase(Number(deal?.down_payment || 0))}</td>
              </tr>
            </tbody>
          </Table>

          {/* Quick jump to other payments */}
          {paymentsAsc.length > 1 && (
            <Card className="mb-3">
              <CardBody>
                <h5 className="text-center mb-3">Other Payments in this Deal</h5>
                <ListGroup>
                  {paymentsAsc.map((p, idx) => {
                    const isCurrent = p?.id === payment?.id
                    const label = [
                      `Payment #${idx + 1}`,
                      p?.reference_number ? `(${p.reference_number})` : null,
                      p?.method_type
                        ? StringUtil.capitalizeAll(StringUtil.humanize(p.method_type))
                        : null,
                      p?.state ? `- ${StringUtil.capitalizeAll(StringUtil.humanize(p.state))}` : null,
                    ]
                      .filter(Boolean)
                      .join(' ')

                    return (
                      <ListGroupItem
                        key={p?.id}
                        active={isCurrent}
                        tag={isCurrent ? 'div' : 'a'}
                        href={isCurrent ? undefined : `/app/finance/payments/${p.id}`}
                        style={{ cursor: isCurrent ? 'default' : 'pointer' }}
                      >
                        {label}
                      </ListGroupItem>
                    )
                  })}
                </ListGroup>
              </CardBody>
            </Card>
          )}
        </div>
      </Card>

      {/* 2) BUKTI UPLOAD (DP dan PEMBAYARAN) */}
      <Card>
        <CardBody>
          <h2 className="mb-4 text-center">Bukti Pembayaran</h2>

          {/* =========================
              🔵 DP RECEIPTS (PO only)
          ========================= */}
          <Alert color="info">Payment #1 biasanya merupakan DP (uang muka). Jika Payment #1 belum memiliki bukti sendiri, bukti DP digunakan otomatis. Nomor Payment menunjukkan urutan pembayaran; nomor Bukti menunjukkan file pada pembayaran tersebut. Konfirmasi hanya berlaku pada payment yang sedang dibuka.</Alert>
          {dpReceiptUrls.length > 0 && !dpShownInPayments && (
            <div style={{ marginBottom: 32 }}>
              <div
                style={{
                  background: "#e7f1ff",
                  padding: "10px 14px",
                  borderRadius: 6,
                  fontWeight: 700,
                  marginBottom: 12
                }}
              >
                Arsip Bukti DP pada Deal
              </div>

              <ListGroup>
                {dpReceiptUrls.map((url, idx) => (
                  <ListGroupItem key={`dp_${idx}`}>
                    <div style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>
                      Bukti DP #{idx + 1}
                    </div>

                    {isPdfUrl(url) ? (
                      <a href={url} target="_blank" rel="noopener noreferrer">
                        Open DP Receipt (PDF)
                      </a>
                    ) : (
                      <img
                        src={url}
                        alt={`DP Proof ${idx + 1}`}
                        style={{
                          width: "100%",
                          maxHeight: 360,
                          objectFit: "contain",
                          borderRadius: 6,
                          border: "1px solid #ddd"
                        }}
                      />
                    )}
                  </ListGroupItem>
                ))}
              </ListGroup>
            </div>
          )}

          {/* =========================
              🟢 PAYMENT RECEIPTS
          ========================= */}
          {receiptUrls.length > 0 && (
            <div>
              <div
                style={{
                  background: "#e8f8f0",
                  padding: "10px 14px",
                  borderRadius: 6,
                  fontWeight: 700,
                  marginBottom: 12
                }}
              >
                {currentReceiptTitle}
              </div>

              <ListGroup>
                {receiptUrls.map((url, idx) => (
                  <ListGroupItem key={`pay_${idx}`}>
                    <div style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>
                      {currentReceiptTitle} — Bukti #{idx + 1}
                    </div>

                    {isPdfUrl(url) ? (
                      <a href={url} target="_blank" rel="noopener noreferrer">
                        Open Payment Receipt (PDF)
                      </a>
                    ) : (
                      <img
                        src={url}
                        alt={`${currentReceiptTitle} — Bukti #${idx + 1}`}
                        style={{
                          width: "100%",
                          maxHeight: 360,
                          objectFit: "contain",
                          borderRadius: 6,
                          border: "1px solid #ddd"
                        }}
                      />
                    )}
                  </ListGroupItem>
                ))}
              </ListGroup>
            </div>
          )}

          {Array.isArray(payment?.deal_payment_receipts) && payment.deal_payment_receipts.some((entry) => entry.receipts?.length > 0) && (
            <div className="mt-4">
              <h4>Semua Bukti Transfer dalam Deal Ini</h4>
              <Alert color="info">Bukti tetap tersimpan pada payment asal. Daftar ini untuk referensi; konfirmasi hanya berlaku pada payment yang sedang dibuka.</Alert>
              {payment.deal_payment_receipts.map((entry) => (
                <div key={entry.payment_id} className="mb-3">
                  <strong>{receiptPaymentTitle(entry)}</strong>
                  {(entry.receipts || []).length === 0 && <div className="text-muted">Belum ada bukti pada payment ini.</div>}
                  {(entry.receipts || []).map((file, index) => (
                    <div key={`${entry.payment_id}-${index}`} className="mt-2">
                      <a href={file.url} target="_blank" rel="noopener noreferrer">Bukti #{index + 1}{file.source === 'deal_dp' ? ' (dari bukti DP)' : ''} — {file.filename || 'buka / unduh'}</a>
                      {file.content_type?.startsWith('image/') && <a href={file.url} target="_blank" rel="noopener noreferrer"><img loading="lazy" src={file.url} alt={`Bukti Payment ${entry.payment_number} — ${index + 1}`} style={{ display: 'block', maxWidth: '100%', maxHeight: 320, objectFit: 'contain' }} /></a>}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
          {dpReceiptUrls.length === 0 && receiptUrls.length === 0 && (
            <Alert color="secondary">Belum ada bukti pada payment yang sedang dibuka. Periksa daftar seluruh payment di atas.</Alert>
          )}
        </CardBody>
      </Card>

      {/* 3) UBAH DOWN PAYMENT */}
      {shouldShowDownPaymentCard && (
        <Card style={{ opacity: isSettlement ? 0.5 : 1 }}>
          <CardBody style={{ pointerEvents: isSettlement ? 'none' : 'auto' }}>
            <h2 className="mb-3 text-center">Ubah Down Payment</h2>

            {isSettlement && (
              <Alert color="secondary" style={{ marginTop: 12 }}>
                🔒 Pembayaran sudah diproses (Settlement).
              </Alert>
            )}

            {currentDpAmount > 0 && !isDpEqualFinal && (
              <Alert color="warning" style={{ marginBottom: 12 }}>
                Nominal saat ini belum lunas. Setelah Finance confirm, deal akan kembali ke <b>waiting_for_payment</b>.
              </Alert>
            )}

            {isDpEqualFinal && (
              <Alert color="success" style={{ marginBottom: 12 }}>
                Nominal saat ini sudah lunas. Setelah Finance confirm, deal akan lanjut ke <b>waiting_for_shipment</b>.
              </Alert>
            )}

            {!hasAnyDownPayment && (
              <Alert color="secondary" style={{ marginBottom: 12 }}>
                Isi nominal yang sudah dibayar customer sebelum melakukan konfirmasi Finance.
              </Alert>
            )}

            <InputGroup>
              <Input
                type='text'
                inputMode='numeric'
                autoComplete='off'
                value={formatThousandsInput(downPayment)}
                onChange={(e) => {
                  const rawValue = sanitizeThousandsInput(e.target.value)
                  const nextValue = rawValue === '' ? 0 : Number(rawValue)

                  setDownPayment(Math.min(Number(deal?.final_payment || 0), nextValue))
                }}
              />
            </InputGroup>

            <p style={{ marginTop: 8 }}>{StringUtil.currencyCase(downPayment)}</p>

            <div
              style={{
                ...actionStyles.singleActionBar,
                pointerEvents: isSettlement ? 'none' : 'auto',
                marginTop: 8
              }}
            >
              <Button color="info" onClick={handleSaveDownPayment} style={actionStyles.button}>
                Simpan
              </Button>
            </div>

            {isSettlement && (
              <Alert color="secondary" style={{ marginTop: 12 }}>
                🔒 Pembayaran sudah settlement. Aksi dinonaktifkan.
              </Alert>
            )}

            <Alert color="secondary" style={{ marginTop: 12 }}>
              Catatan: setelah klik tombol, halaman akan auto refresh agar bukti pembayaran tidak hilang.
            </Alert>
          </CardBody>
        </Card>
      )}

       {/* 4) PO WARNING + 5) PO DOWN PAYMENT */}
      {isPoDpDeal && (
        <Alert color="warning">
          ⚠️ Ini adalah <b>PO Deal</b>. Jangan gunakan <b>Manual Transfer Confirm</b>. Gunakan tombol{' '}
          <b>Confirm (Move to Buying)</b> di bawah.
        </Alert>
      )}

      {canConfirmDp && hasDpProof && !isSettlement && (
        <Card>
          <CardBody>
            <h2 className="mb-3 text-center">PO Down Payment</h2>

            <div style={actionStyles.singleActionBar}>
              <Button color="success" onClick={handleConfirmDpMoveToBuying} style={actionStyles.button}>
                Confirm (Move to Buying)
              </Button>
            </div>

            {isSettlement && (
              <Alert color="secondary">
                🔒 DP sudah diproses.
              </Alert>
            )}
          </CardBody>
        </Card>
      )}

      {/* 6) MANUAL TRANSFER ACTIONS (receipt display already in section #2) */}
      {canShowManualActionButtons(payment?.method_type) && (
        <Card>
          <CardBody>
            <h2 className="mb-3 text-center">
              {StringUtil.capitalizeAll(StringUtil.humanize(payment?.method_type || 'manual_transfer'))}
            </h2>

            <Alert color="secondary">
              Data bank pengirim <b>opsional</b>. Kalau kosong, tetap boleh konfirmasi kalau receipt valid.
            </Alert>

            <ListGroup className="text-sm">
              <ListGroupItem>Nama Bank: {deal?.bank || '-'}</ListGroupItem>
              <ListGroupItem>Nama Akun Bank: {deal?.bank_account_name || '-'}</ListGroupItem>
              <ListGroupItem>No. Akun Bank: {deal?.bank_account_number || '-'}</ListGroupItem>
              <ListGroupItem>Cabang Bank: {deal?.bank_branch || '-'}</ListGroupItem>
            </ListGroup>

            {isPoDpDeal && payment?.state === PAYMENT_STATE.PENDING && (
              <Alert color="warning" style={{ marginTop: 12 }}>
                PO deals harus dikonfirmasi melalui proses <b>Confirm DP</b>.
              </Alert>
            )}

            {/* Actions: keep reject/confirm gated */}
            <div style={actionStyles.actionBar}>
              {payment?.state === PAYMENT_STATE.PENDING &&
                (deal?.state === 'waiting_for_confirmation' || deal?.state === 'waiting_for_payment') &&
                  !isPoDpDeal &&
                  hasAnyFinanceProof && (
                <>
                  <Button color="danger" onClick={handleRejectManual} style={actionStyles.button}>
                    Tolak
                  </Button>
                  <Button color="success" onClick={handleConfirmManual} style={actionStyles.button}>
                    Konfirmasi
                  </Button>
                </>
              )}

              {/* If you still want allow reject even for PO, move it outside !isPoDpDeal */}
            </div>
          </CardBody>
        </Card>
      )}

      {/* 7) HISTORY */}
      <Card>
        <CardBody>
          <div style={actionStyles.singleActionBar}>
            <Button
              color="info"
              style={actionStyles.button}
              onClick={() => setIsHistoryOpen(!isHistoryOpen)}
            >
              Lihat History
            </Button>
          </div>

          <Collapse isOpen={isHistoryOpen}>
            <Card>
              <CardBody>
                <h2 className="mb-3 text-center">History</h2>

                <ListGroup>
                  {(historyList || []).map((history) => {
                    const isActive = history?.id === historyDetail?.id
                    return (
                      <div key={history?.id}>
                        <ListGroupItem
                          action
                          active={isActive}
                          tag="button"
                          onClick={() => {
                            setShowDifferenceOnly(false)
                            if (isActive) {
                              clearHistoryDetail()
                            } else {
                              fetchHistoryDetail(history.id)
                            }
                          }}
                        >
                          {TimeUtil.format(history.created_at, 'YYYY-MM-DD HH:mm:ss')}: ({history.done_by}){' '}
                          {history.action_type.replaceAll('_', ' ')}
                        </ListGroupItem>

                        {isActive && (
                          <>
                            <Label className="text-gray-light text-nowrap mt-2">
                              Lihat field yang berubah saja
                            </Label>
                            <Label className="custom-toggle mb-4">
                              <span></span>
                              <input
                                type="checkbox"
                                value={showDifferenceOnly}
                                onClick={() => setShowDifferenceOnly(!showDifferenceOnly)}
                              />
                              <span
                                className="custom-toggle-slider rounded-circle text-success"
                                data-label-off="No"
                                data-label-on="Yes"
                              />
                            </Label>

                            <Table striped>
                              <thead>
                                <tr>
                                  <th>Field</th>
                                  <th>Before</th>
                                  <th>After</th>
                                </tr>
                              </thead>
                              <tbody>
                                {tableData
                                  .filter((o) => (showDifferenceOnly ? !o.sameValue : true))
                                  .map((o, idx) => (
                                    <tr key={`${o.fieldName}_${idx}`}>
                                      <th scope="row">{o.fieldName}</th>
                                      <td>{o.before}</td>
                                      <td>{o.after}</td>
                                    </tr>
                                  ))}
                              </tbody>
                            </Table>
                          </>
                        )}
                      </div>
                    )
                  })}
                </ListGroup>
              </CardBody>
            </Card>
          </Collapse>
        </CardBody>
      </Card>
    </>
  )
}

FinancePaymentsView.propTypes = propTypes
FinancePaymentsView.defaultProps = defaultValues

export default withLoader(FinancePaymentsView, CardLoader)
